/**
 * RESPONSIBILITY: which directive names a markdown value uses.
 *
 * Detection only — this never parses the document or produces markup. remark does that.
 * All this answers is "which names appear", so the annotation's `allow` list can be
 * enforced on the server without shipping a markdown parser to the write path.
 *
 *   :verse[John 15]        text directive
 *   ::break                leaf directive
 *   :::callout … :::       container directive
 *
 * A name is `[a-z][a-z0-9-]*`, which is all the syntax permits, so the scan cannot be
 * confused into reporting something that is not a directive.
 */

/**
 * The colon must START a token. Without that, `mailto:james` reported a directive named
 * "james" and `Note:this` one named "this" — and because the write path refuses an
 * unlisted name, an ordinary email address in a paragraph was rejected on save.
 *
 * So: preceded by the start of the text, whitespace, or an opening bracket. A backslash
 * is deliberately not in that set, which is what makes `\:verse` an escape.
 */
const DIRECTIVE = /(?:^|[\s(\[{>])::?:?([a-z][a-z0-9-]*)/gim;

/** Every distinct directive name in the text. Escaped ones (`\:verse`) do not count. */
export function usedDirectives(text: string): string[] {
  const names = new Set<string>();
  for (const m of text.matchAll(DIRECTIVE)) names.add(m[1].toLowerCase());
  return [...names];
}

/** Names used that the field did not allow. Empty means the value is legal. */
export const disallowedDirectives = (text: string, allow: readonly string[]): string[] =>
  usedDirectives(text).filter((n) => !allow.includes(n));

/**
 * The class a directive renders with. Derived from the name rather than mapped, so
 * allowing `verse` is the whole of what an app has to do — it then styles `.md-verse`.
 */
export const directiveClass = (name: string): string => `md-${name}`;
